/**
 * @fileoverview Minimal plug-and-play modal custom element
 *
 * @example
 * // Basic usage with internal trigger
 * <modal-element id="my-modal">
 *   <modal-trigger>
 *     <button>Open Modal</button>
 *   </modal-trigger>
 *   <modal-content>
 *     <button class="close">X</button>
 *     <p>Modal content here</p>
 *   </modal-content>
 * </modal-element>
 *
 * @example
 * // External trigger using modal-id
 * <modal-trigger modal-id="my-modal">
 *   <button>Open Modal</button>
 * </modal-trigger>
 *
 * @example
 * // Programmatic control via window events
 * window.dispatchEvent(new Event('open-modal-my-modal'));
 * window.dispatchEvent(new Event('close-modal-my-modal'));
 */

const MODAL_STYLES = `
  :host { display: contents; }

  .backdrop {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.3s ease, visibility 0.3s ease;
  }

  .backdrop.open {
    visibility: visible;
    opacity: 1;
  }

  .modal-content {
    background: var(--modal-bg, #fff);
    max-width: 100%;
    max-height: 100%;
    overflow-y: visible;
    transform: scale(0.95);
    opacity: 0;
    transition: transform 0.3s ease, opacity 0.3s ease;
  }

  .backdrop.open .modal-content {
    transform: scale(1);
    opacity: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    .backdrop, .modal-content {
      transition: none;
    }
  }
`;

class ModalElement extends HTMLElement {
  /** @type {HTMLElement|null} */
  #backdrop = null;
  #boundOpen = null;
  #boundClose = null;
  #boundKeydown = null;
  /** @type {Element|null} Element to return focus to on close */
  #triggerEl = null;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot.innerHTML = `
      <style>${MODAL_STYLES}</style>
      <slot name="trigger"></slot>
      <div class="backdrop">
        <div class="modal-content">
          <slot name="content"></slot>
        </div>
      </div>
    `;

    // Bind methods for proper cleanup
    this.#boundOpen = () => this.open();
    this.#boundClose = () => this.close();
    this.#boundKeydown = (e) => { if (e.key === 'Escape') this.close(); };
  }

  connectedCallback() {
    this.#backdrop = this.shadowRoot.querySelector('.backdrop');

    // Move to body for proper stacking
    if (!this.parentElement?.isSameNode(document.body)) {
      document.body.appendChild(this);
    }

    // Assign slots
    this.querySelector('modal-trigger')?.setAttribute('slot', 'trigger');
    this.querySelector('modal-content')?.setAttribute('slot', 'content');

    // Static ARIA — this is always a dialog
    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-modal', 'true');

    // Wire aria-labelledby to the first heading inside modal-content
    const content = this.querySelector('modal-content');
    if (content) {
      const heading = content.querySelector('h1, h2, h3, h4, h5, h6');
      if (heading) {
        if (!heading.id) heading.id = `modal-heading-${this.id || 'default'}`;
        this.setAttribute('aria-labelledby', heading.id);
      }
    }

    // Internal trigger click
    this.querySelector('modal-trigger')?.addEventListener('click', this.#boundOpen);

    // Backdrop click to close
    this.#backdrop?.addEventListener('click', (e) => {
      if (e.target === this.#backdrop) this.close();
    });

    // Close button inside content
    this.querySelector('.close')?.addEventListener('click', this.#boundClose);

    // Window events for external control
    const id = this.id;
    if (id) {
      window.addEventListener(`open-modal-${id}`, this.#boundOpen);
      window.addEventListener(`close-modal-${id}`, this.#boundClose);
    }
  }

  disconnectedCallback() {
    const id = this.id;
    if (id) {
      window.removeEventListener(`open-modal-${id}`, this.#boundOpen);
      window.removeEventListener(`close-modal-${id}`, this.#boundClose);
    }
    document.removeEventListener('keydown', this.#boundKeydown);
  }

  /** Opens the modal */
  open() {
    this.#triggerEl = document.activeElement;
    this.#backdrop?.classList.add('open');
    this.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.addEventListener('keydown', this.#boundKeydown);

    // Move focus to the first focusable element inside the modal
    const focusable = this.querySelector('modal-content')?.querySelector(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    focusable?.focus();

    this.dispatchEvent(new CustomEvent('modal:open', { bubbles: true }));
  }

  /** Closes the modal */
  close() {
    this.#backdrop?.classList.remove('open');
    this.removeAttribute('open');
    document.removeEventListener('keydown', this.#boundKeydown);

    // Only restore scroll if no other drawers/modals are open
    const openElements = document.querySelectorAll('drawer-element[open], modal-element[open]');
    if (openElements.length === 0) {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }

    // Return focus to the element that triggered the modal
    this.#triggerEl?.focus();
    this.#triggerEl = null;

    this.dispatchEvent(new CustomEvent('modal:close', { bubbles: true }));
  }

  /** @returns {boolean} Whether the modal is open */
  get isOpen() {
    return this.#backdrop?.classList.contains('open') ?? false;
  }
}

class ModalTrigger extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', () => {
      const modalId = this.getAttribute('modal-id');
      if (!modalId) return;

      const modal = document.getElementById(modalId);
      if (modal?.isOpen) {
        window.dispatchEvent(new Event(`close-modal-${modalId}`));
      } else {
        window.dispatchEvent(new Event(`open-modal-${modalId}`));
      }
    });
  }
}

class ModalContent extends HTMLElement {
  connectedCallback() {
    this.setAttribute('slot', 'content');
  }
}

customElements.define('modal-element', ModalElement);
customElements.define('modal-trigger', ModalTrigger);
customElements.define('modal-content', ModalContent);
